// A question mark beside a figure, opening a pop-up with the formula that reached it, typeset
// (TX-TSK-0126).
//
// The pop-up is a native popover: it sits in the top layer, so the table's scroll box does not
// clip it, and Escape and a click elsewhere close it without a line of code here. It is placed
// beside its figure once open - above it where there is room, below where there is not - and
// follows it while the page or the table scrolls. Its content follows the sliders live.
//
// KaTeX is loaded the first time any pop-up opens, not with the page: the table is read far more
// often than it is questioned.

import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';

import Icon from '../ui/Icon.jsx';

let katexLoading;
const loadKatex = () =>
  (katexLoading ??= Promise.all([import('katex'), import('katex/dist/katex.min.css')]).then(
    ([module]) => module.default,
  ));

// Clear of the figure, and of the window's edges.
const GAP = 14;
const MARGIN = 16;

function place(anchor, pop) {
  const at = anchor.getBoundingClientRect();
  // Its laid-out size: the bounding box would be the pop-in animation's scaled one.
  const { offsetWidth: width, offsetHeight: height } = pop;
  const above = at.top - GAP - height >= MARGIN;
  const centre = at.left + at.width / 2;
  const left = Math.min(Math.max(centre - width / 2, MARGIN), window.innerWidth - MARGIN - width);
  pop.style.top = `${above ? at.top - GAP - height : at.bottom + GAP}px`;
  pop.style.left = `${left}px`;
  pop.style.setProperty('--tail-x', `${centre - left}px`);
  pop.dataset.side = above ? 'above' : 'below';
}

export default function FormulaTip({ caption, title, tex }) {
  const id = useId();
  const button = useRef(null);
  const pop = useRef(null);
  const [open, setOpen] = useState(false);
  const [html, setHtml] = useState(null);

  useEffect(() => {
    const element = pop.current;
    const onToggle = (event) => {
      if (event.newState !== 'open') delete element.dataset.side;
      setOpen(event.newState === 'open');
    };
    element.addEventListener('toggle', onToggle);
    return () => element.removeEventListener('toggle', onToggle);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    let live = true;
    loadKatex().then((katex) => {
      if (live) setHtml(katex.renderToString(tex, { displayMode: true, throwOnError: false }));
    });
    return () => {
      live = false;
    };
  }, [open, tex]);

  useLayoutEffect(() => {
    if (!open) return undefined;
    const follow = () => place(button.current, pop.current);
    follow();
    // KaTeX's fonts arrive after its first render and widen the formula: placed again as they do.
    const resized = new ResizeObserver(follow);
    resized.observe(pop.current);
    window.addEventListener('scroll', follow, true);
    window.addEventListener('resize', follow);
    return () => {
      resized.disconnect();
      window.removeEventListener('scroll', follow, true);
      window.removeEventListener('resize', follow);
    };
  }, [open]);

  return (
    <>
      <button
        ref={button}
        type="button"
        className="formula-tip"
        popoverTarget={id}
        aria-label={`How this ${caption.toLowerCase()} is calculated`}
      >
        <Icon name="help" />
      </button>
      <div ref={pop} id={id} popover="auto" className="formula-pop" role="dialog" aria-label={title}>
        <div className="formula-pop-head">
          <span className="formula-pop-caption">{caption}</span>
          <strong>{title}</strong>
        </div>
        {html ? (
          <div className="formula-pop-math" dangerouslySetInnerHTML={{ __html: html }} />
        ) : (
          <div className="formula-pop-math formula-pop-loading">Working it out…</div>
        )}
      </div>
    </>
  );
}
